// Events tab — emit any ConnectionEventMap event by hand. Lets the dev exercise
// wiring's event subscribers without orchestrating real preconditions inside the
// SDK iframe. Grouped by category so the layout stays scannable when the event
// surface grows.

import { compose, mdsWidget } from 'dojo/_base/declare';
import template from './simulatorEventsTab.html?raw';
import lang from 'dojo/_base/lang';
import _WidgetBaseSP from 'more/_WidgetBaseSP';

import type { ConnectionEventMap, EvisioErrorKind } from "../../../sdk/evisio2sdk";
import { MockEvisio2Sdk } from "../../mockSdk/mockEvisio2Sdk";
import { MockEvisio2Connection } from "../../mockSdk/mockEvisio2Connection";
import { MockEvisioError } from "../../mockSdk/mockEvisioError";

// Per-event row config. `argsBuilder` reads the inline form values and assembles the
// event payload; `buildForm` renders the inputs into the action row.
interface EventRowConfig {
    event: keyof ConnectionEventMap;
    label: string;
    description: string;
    buildForm?: (container: HTMLElement) => void;
    argsBuilder: (container: HTMLElement) => any;
}

class simulatorEventsTab extends compose(_WidgetBaseSP) {
    templateString: any = template;

    public sdk: MockEvisio2Sdk;
    public connection: MockEvisio2Connection;

    private rootContainer: HTMLDivElement;

    constructor(args?: { sdk: MockEvisio2Sdk; connection: MockEvisio2Connection }){
        super(...(arguments as unknown as []));
        if (args){ this.sdk = args.sdk; this.connection = args.connection; }
    }

    postCreate(): void {
        super.postCreate.apply(this, arguments as any);
        this._renderGroups();
    }

    startup(): void { super.startup.apply(this, arguments as any); }

    private _renderGroups(): void {
        let groups: Array<{ title: string; rows: EventRowConfig[] }> = [
            {
                title: "Connection lifecycle",
                rows: [
                    this._rowReady(),
                    this._rowSignedIn(),
                    this._rowSignedOut(),
                    this._rowConnectionLost(),
                    this._rowResumed(),
                    this._rowDisconnected(),
                ],
            },
            {
                title: "Thread / messages",
                rows: [
                    this._rowThreadJoined(),
                    this._rowMessageReceived(),
                    this._rowUserMessageSent(),
                ],
            },
            {
                title: "Tool invocation",
                rows: [
                    this._rowToolInvocationStarted(),
                    this._rowToolInvocationFinished(),
                    this._rowToolCall(),
                ],
            },
            {
                title: "Artifacts",
                rows: [
                    this._rowArtifactProduced(),
                ],
            },
            {
                title: "Errors",
                rows: [
                    this._rowError(),
                ],
            },
        ];

        for (let i = 0; i < groups.length; i++){
            let g = groups[i];
            let groupEl = document.createElement("details");
            groupEl.style.cssText = "background:#16171a; border:1px solid #2a2b30; border-radius:3px; padding:6px 8px;";
            if (i === 0) groupEl.open = true; // open the first group by default
            let title = document.createElement("summary");
            title.style.cssText = "cursor:pointer; font-weight:600; opacity:0.8; font-size:11px;";
            title.textContent = g.title;
            groupEl.appendChild(title);
            let rowsWrap = document.createElement("div");
            rowsWrap.style.cssText = "display:flex; flex-direction:column; gap:4px; margin-top:6px;";
            for (let j = 0; j < g.rows.length; j++){
                rowsWrap.appendChild(this._renderRow(g.rows[j]));
            }
            groupEl.appendChild(rowsWrap);
            this.rootContainer.appendChild(groupEl);
        }
    }

    // Per-event card. Header (name + emit button) on top row; description + inline form
    // below. Compact enough for the narrow simulator slot but still readable.
    private _renderRow(cfg: EventRowConfig): HTMLElement {
        let row = document.createElement("div");
        row.style.cssText = "display:flex; flex-direction:column; gap:3px; padding:4px 6px; background:#1f2024; border-radius:3px;";

        let topRow = document.createElement("div");
        topRow.style.cssText = "display:flex; gap:6px; align-items:center; justify-content:space-between;";
        let name = document.createElement("div");
        name.textContent = cfg.event;
        name.style.cssText = "font-weight:600; font-family:Consolas,monospace; font-size:11px;";

        let btn = document.createElement("button");
        btn.type = "button";
        btn.textContent = "emit";
        btn.style.cssText = "background:#3a7afe; color:white; border:0; border-radius:3px; padding:3px 10px; cursor:pointer; font-family:inherit; font-size:11px;";

        topRow.appendChild(name);
        topRow.appendChild(btn);

        let desc = document.createElement("div");
        desc.textContent = cfg.description;
        desc.style.cssText = "opacity:0.55; font-size:10px;";

        let form = document.createElement("div");
        form.style.cssText = "display:flex; gap:4px; flex-wrap:wrap; align-items:center;";
        if (cfg.buildForm) cfg.buildForm(form);

        btn.addEventListener("click", lang.hitch(this, () => {
            let args = cfg.argsBuilder(form);
            this.connection._emit(cfg.event, args);
        }));

        row.appendChild(topRow);
        row.appendChild(desc);
        if (form.children.length > 0) row.appendChild(form);
        return row;
    }

    // ─── Row builders ──────────────────────────────────────────────────────

    private _rowReady(): EventRowConfig {
        return {
            event: "ready",
            label: "ready",
            description: "Sticky — late subscribers replay once",
            argsBuilder: () => ({}),
        };
    }

    private _rowSignedIn(): EventRowConfig {
        let userHashInput: HTMLInputElement;
        return {
            event: "signedIn",
            label: "signedIn",
            description: "Sticky — sets userHash on the connection",
            buildForm: (c) => {
                userHashInput = this._mkInput(c, "userHash", "user-abc123");
            },
            argsBuilder: () => ({ userHash: userHashInput.value || "user-abc123" }),
        };
    }

    private _rowSignedOut(): EventRowConfig {
        return { event: "signedOut", label: "signedOut", description: "Clears userHash; transport stays up", argsBuilder: () => ({}) };
    }
    private _rowConnectionLost(): EventRowConfig {
        return { event: "connectionLost", label: "connectionLost", description: "Ping timeout — state → suspended", argsBuilder: () => ({}) };
    }
    private _rowResumed(): EventRowConfig {
        return { event: "resumed", label: "resumed", description: "Recovery — state → connected", argsBuilder: () => ({}) };
    }
    private _rowDisconnected(): EventRowConfig {
        return { event: "disconnected", label: "disconnected", description: "Terminal — state → disconnected", argsBuilder: () => ({}) };
    }

    private _rowThreadJoined(): EventRowConfig {
        let threadIdInput: HTMLInputElement;
        return {
            event: "threadJoined",
            label: "threadJoined",
            description: "Pass 'new' for composing-fresh, a uuid for a real thread",
            buildForm: (c) => { threadIdInput = this._mkInput(c, "threadId", "new"); },
            argsBuilder: () => ({ threadId: threadIdInput.value || "new" }),
        };
    }

    private _rowMessageReceived(): EventRowConfig {
        let threadInput: HTMLInputElement;
        let msgIdInput: HTMLInputElement;
        let textInput: HTMLInputElement;
        return {
            event: "messageReceived",
            label: "messageReceived",
            description: "Assistant message committed",
            buildForm: (c) => {
                threadInput = this._mkInput(c, "threadId", "");
                msgIdInput  = this._mkInput(c, "messageId", "msg-1");
                textInput   = this._mkInput(c, "text", "hello from the assistant", 240);
            },
            argsBuilder: () => ({
                threadId: threadInput.value || this.connection._getCurrentThreadId() || "",
                messageId: msgIdInput.value || "msg-1",
                text: textInput.value || "",
            }),
        };
    }

    private _rowUserMessageSent(): EventRowConfig {
        let threadInput: HTMLInputElement;
        let msgIdInput: HTMLInputElement;
        let textInput: HTMLInputElement;
        return {
            event: "userMessageSent",
            label: "userMessageSent",
            description: "User-side optimistic-insert moment",
            buildForm: (c) => {
                threadInput = this._mkInput(c, "threadId", "");
                msgIdInput  = this._mkInput(c, "messageId", "tmp-1");
                textInput   = this._mkInput(c, "text", "do the thing", 240);
            },
            argsBuilder: () => ({
                threadId: threadInput.value || this.connection._getCurrentThreadId() || "",
                messageId: msgIdInput.value || "tmp-1",
                text: textInput.value || "",
            }),
        };
    }

    private _rowToolInvocationStarted(): EventRowConfig {
        let nameInput: HTMLInputElement;
        let toolCallIdInput: HTMLInputElement;
        let threadInput: HTMLInputElement;
        let isSdkInput: HTMLInputElement;
        return {
            event: "toolInvocationStarted",
            label: "toolInvocationStarted",
            description: "Agent began executing a tool",
            buildForm: (c) => {
                nameInput        = this._mkInput(c, "name", "external_openEntityView");
                toolCallIdInput  = this._mkInput(c, "toolCallId", "call-1");
                threadInput      = this._mkInput(c, "threadId", "");
                isSdkInput       = this._mkCheckbox(c, "isSdkTool", true);
            },
            argsBuilder: () => ({
                name: nameInput.value || "external_openEntityView",
                toolCallId: toolCallIdInput.value || "call-1",
                threadId: threadInput.value || this.connection._getCurrentThreadId() || "",
                isSdkTool: isSdkInput.checked,
            }),
        };
    }

    private _rowToolInvocationFinished(): EventRowConfig {
        let nameInput: HTMLInputElement;
        let toolCallIdInput: HTMLInputElement;
        let threadInput: HTMLInputElement;
        let isSdkInput: HTMLInputElement;
        let statusSelect: HTMLSelectElement;
        return {
            event: "toolInvocationFinished",
            label: "toolInvocationFinished",
            description: "Agent finished a tool call (paired with started by toolCallId)",
            buildForm: (c) => {
                nameInput        = this._mkInput(c, "name", "external_openEntityView");
                toolCallIdInput  = this._mkInput(c, "toolCallId", "call-1");
                threadInput      = this._mkInput(c, "threadId", "");
                isSdkInput       = this._mkCheckbox(c, "isSdkTool", true);
                statusSelect     = this._mkSelect(c, "status", ["completed", "faulted", "userDenied", "timedOut"], "completed");
            },
            argsBuilder: () => ({
                name: nameInput.value || "external_openEntityView",
                toolCallId: toolCallIdInput.value || "call-1",
                threadId: threadInput.value || this.connection._getCurrentThreadId() || "",
                isSdkTool: isSdkInput.checked,
                status: statusSelect.value as ("completed" | "faulted" | "userDenied" | "timedOut"),
            }),
        };
    }

    private _rowToolCall(): EventRowConfig {
        let nameInput: HTMLInputElement;
        let durationInput: HTMLInputElement;
        let resultInput: HTMLInputElement;
        return {
            event: "toolCall",
            label: "toolCall",
            description: "External-tool round-trip completed (host-registered tools only)",
            buildForm: (c) => {
                nameInput     = this._mkInput(c, "name", "external_openEntityView");
                durationInput = this._mkInput(c, "durationMs", "120");
                resultInput   = this._mkInput(c, "result", "", 240);
            },
            argsBuilder: () => ({
                name: nameInput.value || "external_openEntityView",
                args: {},
                durationMs: parseInt(durationInput.value || "120", 10) || 0,
                result: resultInput.value || undefined,
                error: undefined,
            }),
        };
    }

    private _rowArtifactProduced(): EventRowConfig {
        let threadInput: HTMLInputElement;
        let fileIdInput: HTMLInputElement;
        let nameInput: HTMLInputElement;
        let mimeInput: HTMLInputElement;
        let sizeInput: HTMLInputElement;
        return {
            event: "artifactProduced",
            label: "artifactProduced",
            description: "Output file landed on a committed assistant message",
            buildForm: (c) => {
                threadInput = this._mkInput(c, "threadId", "");
                fileIdInput = this._mkInput(c, "fileId", "artifact-1");
                nameInput   = this._mkInput(c, "name", "output.pdf");
                mimeInput   = this._mkInput(c, "mime", "application/pdf");
                sizeInput   = this._mkInput(c, "size", "1024");
            },
            argsBuilder: () => ({
                threadId: threadInput.value || this.connection._getCurrentThreadId() || "",
                fileId: fileIdInput.value || "artifact-1",
                name: nameInput.value || "output.pdf",
                mime: mimeInput.value || undefined,
                size: parseInt(sizeInput.value || "0", 10) || undefined,
            }),
        };
    }

    private _rowError(): EventRowConfig {
        let kindSelect: HTMLSelectElement;
        let messageInput: HTMLInputElement;
        let toolInput: HTMLInputElement;
        let codeInput: HTMLInputElement;
        let kinds: EvisioErrorKind[] = ["aborted", "popupBlocked", "disconnected", "timeout", "handlerThrew", "notRegistered", "fileTooLarge", "fileCleaned", "notFound", "uploadFailed", "detachFailed", "unsupported"];
        return {
            event: "error",
            label: "error",
            description: "Connection-level error event (carries an EvisioError)",
            buildForm: (c) => {
                kindSelect   = this._mkSelect(c, "kind", kinds as string[], "disconnected");
                messageInput = this._mkInput(c, "message", "error from simulator", 200);
                toolInput    = this._mkInput(c, "toolName", "");
                codeInput    = this._mkInput(c, "code", "");
            },
            argsBuilder: () => {
                let kind = kindSelect.value as EvisioErrorKind;
                let extras: { toolName?: string; code?: string } = {};
                if (toolInput.value) extras.toolName = toolInput.value;
                if (codeInput.value) extras.code = codeInput.value;
                let err = new MockEvisioError(kind, messageInput.value || ("error: " + kind), extras);
                return { error: err as any };
            },
        };
    }

    // ─── Tiny form helpers ─────────────────────────────────────────────────

    private _mkInput(container: HTMLElement, label: string, placeholder: string, widthPx?: number): HTMLInputElement {
        let wrap = document.createElement("label");
        wrap.style.cssText = "display:flex; gap:3px; align-items:center; font-size:10px;";
        let lab = document.createElement("span"); lab.textContent = label; lab.style.cssText = "opacity:0.6;";
        let input = document.createElement("input");
        input.type = "text";
        input.placeholder = placeholder;
        input.style.cssText = "background:#16171a; color:#e6e6e6; border:1px solid #2a2b30; border-radius:3px; padding:2px 5px; font-family:Consolas,monospace; font-size:10px;" + (widthPx ? " width:" + widthPx + "px;" : " width:90px;");
        wrap.appendChild(lab); wrap.appendChild(input);
        container.appendChild(wrap);
        return input;
    }

    private _mkCheckbox(container: HTMLElement, label: string, defaultValue: boolean): HTMLInputElement {
        let wrap = document.createElement("label");
        wrap.style.cssText = "display:flex; gap:3px; align-items:center; font-size:10px;";
        let lab = document.createElement("span"); lab.textContent = label; lab.style.cssText = "opacity:0.6;";
        let input = document.createElement("input");
        input.type = "checkbox";
        input.checked = defaultValue;
        wrap.appendChild(input); wrap.appendChild(lab);
        container.appendChild(wrap);
        return input;
    }

    private _mkSelect(container: HTMLElement, label: string, options: string[], defaultValue: string): HTMLSelectElement {
        let wrap = document.createElement("label");
        wrap.style.cssText = "display:flex; gap:3px; align-items:center; font-size:10px;";
        let lab = document.createElement("span"); lab.textContent = label; lab.style.cssText = "opacity:0.6;";
        let sel = document.createElement("select");
        sel.style.cssText = "background:#16171a; color:#e6e6e6; border:1px solid #2a2b30; border-radius:3px; padding:2px 5px; font-family:Consolas,monospace; font-size:10px;";
        for (let i = 0; i < options.length; i++){
            let o = document.createElement("option"); o.value = options[i]; o.textContent = options[i]; sel.appendChild(o);
        }
        sel.value = defaultValue;
        wrap.appendChild(lab); wrap.appendChild(sel);
        container.appendChild(wrap);
        return sel;
    }
}

export default mdsWidget(simulatorEventsTab);
